import { convertTimeStamp, downloadBySource, formatDate, formatVerificationType, translateDepartment } from "../../utils/common";
import { DocumentIcon, DownloadIcon } from "../Icons";
import UserNameWithId from "@components/UI/UserNameWithId";
import { useState, useEffect, useRef } from "react";

const BasicInfoSection = ({
  data,
  colors,
  t,
  type,
  onDownloadNotice,
  onDownloadNoticeText,
  onDownloadNoticeHtml,
  onViewNoticeInfo,
  "data-testid": dataTestId = "basic-info-section",
}) => {
  const tid = (suffix) => `${dataTestId}-${suffix}`;
  const fieldLabelTestId = (name) => tid(`field-${name}-label`);
  const fieldValueTestId = (name) => tid(`field-${name}-value`);
  const fieldContainerTestId = (name) => tid(`field-${name}-container`);

  const {
    verificationVersion,
    requesterDeptCdName,
    realFileName,
    requesterName,
    requesterId,
    binaryVerificationSeq,
    sourceCodeVerificationSeq,
    sbomVerificationSeq,
    clarityAirVerificationSeq,
    projectName,
    requestedAt,
    updatedAt,
    reverificationDueDt,
    scanTypeCd,
    vendorCompanyName,
    vendorSubmissionDate,
    vendorContactName,
    vendorContactEmail,
    vendorAddress,
    // Notice information fields
    noticeInfoId,
    writeCompletionExpectedDt,
    noticeInfoWriteStatusCdName,
  } = data;

  // State for download format selection
  const [downloadFormat, setDownloadFormat] = useState("text");
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Determine download parameters based on type
  const handleDownload = () => {
    if (!realFileName) return;
    if (type === "sbom") {
      downloadBySource(realFileName, "downloadBySource", "sbom", sbomVerificationSeq);
    } else if (type === "fossid") {
      downloadBySource(realFileName, "downloadBySource", "source_code", sourceCodeVerificationSeq, data?.repositoryImportFlag || "N");
    } else if (type === "clarity") {
      downloadBySource(realFileName, "downloadBySource", "binary", binaryVerificationSeq, data?.repositoryImportFlag || "N");
    } else if (type === "clarityair") {
      downloadBySource(realFileName, "downloadBySource", "clarity_air", clarityAirVerificationSeq, data?.repositoryImportFlag || "N");
    }
  };

  const formattedRequestedAt = requestedAt ? convertTimeStamp(requestedAt) : "-";
  const formattedUpdatedAt = updatedAt ? convertTimeStamp(updatedAt) : "-";
  const formattedWriteCompletionDate = writeCompletionExpectedDt ? formatDate(writeCompletionExpectedDt) : "-";
  const formattedReverificationDueDt = reverificationDueDt ? formatDate(reverificationDueDt) : "-";
  const formattedVendorSubmissionDate = vendorSubmissionDate ? formatDate(vendorSubmissionDate) : "-";
  const showVendorInfo =
    type === "sbom" &&
    (scanTypeCd === "Excel" ||
      vendorCompanyName ||
      vendorSubmissionDate ||
      vendorContactName ||
      vendorContactEmail ||
      vendorAddress);

  const verificationTypeKey = binaryVerificationSeq
    ? "clarity"
    : sourceCodeVerificationSeq
      ? "fossid"
      : clarityAirVerificationSeq
        ? "clarityair"
        : "sbom";

  // Check if notice data exists
  const hasNoticeData =
    noticeInfoId || noticeInfoWriteStatusCdName || writeCompletionExpectedDt;

  // Handle View button click for notice info
  const handleViewNoticeInfo = () => {
    if (onViewNoticeInfo) {
      onViewNoticeInfo();
    }
  };

  // Handle format selection
  const handleFormatSelect = (format) => {
    setDownloadFormat(format);
    setShowDownloadDropdown(false);
    // Trigger download immediately after format selection
    if (format === "text" && onDownloadNoticeText) {
      onDownloadNoticeText();
    } else if (format === "html" && onDownloadNoticeHtml) {
      onDownloadNoticeHtml();
    }
  };

  // Custom status rendering function for notice information
  const renderNoticeStatus = (status) => {
    const WRITING_STATUS = {
      "REQUEST_WRITING": "Request Writing",
      "COMPLETED": "Completed",
      "WRITE_COMPLETED": "Write Completed",
      "NO_NEED_TO_WRITE": "No Need to Write"
    }

    const statusStyles = {
      [WRITING_STATUS.REQUEST_WRITING]: colors.textWarning,
      [WRITING_STATUS.COMPLETED]: colors.textStatusSuccess,
      [WRITING_STATUS.WRITE_COMPLETED]: colors.textStatusSuccess,
      [WRITING_STATUS.NO_NEED_TO_WRITE]: colors.iconMuted,
    };
    const statusColorDot = {
      [WRITING_STATUS.REQUEST_WRITING]: colors.bgStatusWarning,
      [WRITING_STATUS.COMPLETED]: colors.bgStatusSuccess,
      [WRITING_STATUS.WRITE_COMPLETED]: colors.bgStatusSuccess,
      [WRITING_STATUS.NO_NEED_TO_WRITE]: colors.bgStatusMuted,
    };

    if (status === WRITING_STATUS.WRITE_COMPLETED) {
      return (
        <div className="flex items-center space-x-4" data-testid={tid("notice-status-actions")}>
          <span
            className={`inline-flex items-center text-sm font-medium ${statusStyles[status]}`}
            data-testid={tid("notice-status-badge")}
          >
            <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[status]}`} data-testid={tid("notice-status-dot")}></div>
            {t(`project.notice-infor.notice-writing-status-${status?.toLowerCase() || ''}`, status)}
          </span>

          <button
            type="button"
            onClick={handleViewNoticeInfo}
            data-testid={tid("notice-view-button")}
            className={`inline-flex items-center text-sm font-medium ${colors.textActive} hover:${colors.textHoverActive} transition-colors duration-200`}
          >
            <DocumentIcon className="mr-1 h-4 w-4" data-testid={tid("notice-view-icon")} />
            {t("common.view-button")}
          </button>

          <div className={`w-px h-4 ${colors.headerDivider}`} data-testid={tid("notice-actions-separator")}></div>

          <div className="relative flex items-center" ref={dropdownRef} data-testid={tid("notice-download-container")}>
            <button
              type="button"
              onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
              data-testid={tid("notice-download-toggle-button")}
              className={`inline-flex items-center text-sm font-medium ${colors.textActive} hover:${colors.textHoverActive} transition-colors duration-200`}
            >
              <DownloadIcon className="mr-1 h-4 w-4" data-testid={tid("notice-download-icon")} />
              {t("common.download")}
              <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-testid={tid("notice-download-chevron")}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showDownloadDropdown && (
              <div
                className={`absolute left-0 top-4 mt-2 w-36 rounded-lg shadow-xl ${colors.bgModal} ${colors.borderPrimary} border z-10 overflow-hidden`}
                data-testid={tid("notice-download-dropdown")}
              >
                <div className="py-1" data-testid={tid("notice-download-dropdown-menu")}>
                  <button
                    type="button"
                    onClick={() => handleFormatSelect("text")}
                    data-testid={tid("notice-download-text-button")}
                    className={`group block w-full text-left px-4 py-2.5 text-sm ${colors.textPrimary} transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:shadow-sm relative overflow-hidden ${colors.buttonBackgroundGradientHover} ${colors.hoverTextButton}`}
                    style={{
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <span className="relative z-10 flex items-center font-medium">
                      <svg className="mr-2 h-4 w-4 opacity-60 group-hover:opacity-100 transition-opacity duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t("project.notice-infor.download-by-text", "Text")}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFormatSelect("html")}
                    data-testid={tid("notice-download-html-button")}
                    className={`group block w-full text-left px-4 py-2.5 text-sm ${colors.textPrimary} transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:shadow-sm relative overflow-hidden ${colors.buttonBackgroundGradientHover} ${colors.hoverTextButton}`}
                    style={{
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <span className="relative z-10 flex items-center font-medium">
                      <svg className="mr-2 h-4 w-4 opacity-60 group-hover:opacity-100 transition-opacity duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                      {t("project.notice-infor.download-by-html", "Html")}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <span
        className={`inline-flex items-center text-sm font-medium ${statusStyles[status] || colors.textPrimary}`}
        data-testid={tid("notice-status-badge")}
      >
        {status && <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[status] || colors.bgStatusMuted}`} data-testid={tid("notice-status-dot")}></div>}
        { status ? t(`project.notice-infor.notice-writing-status-${status?.toLowerCase() || ''}`, status) : "-"}
      </span>
    );
  };

  return (
    <div className={`${colors.bgBasicInfo} mx-12 p-4 rounded-t-2xl min-w-[832px]`} data-testid={dataTestId}>
      {hasNoticeData ? (
        <div className="grid grid-cols-12" data-testid={tid("notice-grid")}>
          <div className="col-span-4" data-testid={fieldContainerTestId("notice-id")}>
            <label style={{ fontSize: "0.8125rem" }} className={`font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("notice-id")}>
              {t("project.notice-infor.notice-infor-id")}
            </label>
            <p className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("notice-id")}>{noticeInfoId || "-"}</p>
          </div>

          <div className="col-span-4" data-testid={fieldContainerTestId("write-completion-date")}>
            <label style={{ fontSize: "0.8125rem" }} className={`font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("write-completion-date")}>
              {t("project.notice-infor.notice-write-completion-expected-date")}
            </label>
            <p className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("write-completion-date")}>{formattedWriteCompletionDate || "-"}</p>
          </div>

          <div className="col-span-4" data-testid={fieldContainerTestId("notice-write-status")}>
            <label style={{ fontSize: "0.8125rem" }} className={`font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("notice-write-status")}>
              {t("project.notice-infor.notice-write")}
            </label>
            <div className="mt-1" data-testid={fieldValueTestId("notice-write-status")}>{renderNoticeStatus(noticeInfoWriteStatusCdName)}</div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 2xl:grid-cols-5" data-testid={tid("verification-grid")}>
          <div className="flex items-center" data-testid={fieldContainerTestId("version")}>
            <label style={{ fontSize: "0.8125rem" }} className={`w-25 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("version")}>
              {t("common.version")}
            </label>
            <p className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("version")}>{verificationVersion}</p>
          </div>
          <div className="flex items-center" data-testid={fieldContainerTestId("verification-type")}>
            <label style={{ fontSize: "0.8125rem" }} className={`w-24 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("verification-type")}>
              {t("common.verification-type")}
            </label>
            <p className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("verification-type")}>
              {formatVerificationType(verificationTypeKey)}
            </p>
          </div>
          <div className="flex min-w-0 items-center gap-2" data-testid={fieldContainerTestId("requester")}>
            <label style={{ fontSize: "0.8125rem" }} className={`w-24 shrink-0 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("requester")}>
              {t("common.requester")}
            </label>
            <UserNameWithId
              name={requesterName}
              id={requesterId}
              layout="inline"
              className="min-w-0 flex-1 font-medium"
              data-testid={fieldValueTestId("requester")}
            />
          </div>
          <div className="flex items-center" data-testid={fieldContainerTestId("requested-at")}>
            <label style={{ fontSize: "0.8125rem" }} className={`w-24 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("requested-at")}>
              {t("common.requested-at")}
            </label>
            <p className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("requested-at")}>{formattedRequestedAt}</p>
          </div>
          <div className="flex items-center gap-4" data-testid={fieldContainerTestId("project-name")}>
            <label style={{ fontSize: "0.8125rem" }} className={`w-24 font-normal whitespace-nowrap ${colors.textSubTitle}`} data-testid={fieldLabelTestId("project-name")}>
              {t("common.project-name")}
            </label>
            <p
              className={`min-w-0 flex-1 text-base font-semibold truncate ${colors.textActive}`}
              title={projectName}
              data-testid={fieldValueTestId("project-name")}
            >
              {projectName || "-"}
            </p>
          </div>
          <div className="flex items-center" data-testid={fieldContainerTestId("real-file-name")}>
            <label style={{ fontSize: "0.8125rem" }} className={`w-25 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("real-file-name")}>
              {t("common.real-file-name")}
            </label>
            <p style={{ width: "7rem" }} className={`text-sm ellipsis font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("real-file-name")}>
              {realFileName}
            </p>
            {realFileName && (
              <button
                type="button"
                className={`ml-2 ${colors.textActive}`}
                title={t("common.download")}
                onClick={handleDownload}
                data-testid={tid("real-file-download-button")}
              >
                <DownloadIcon className="h-5 w-5" data-testid={tid("real-file-download-icon")} />
              </button>
            )}
          </div>
          {/* <div className="flex items-center" data-testid={fieldContainerTestId("department")}>
            <label style={{ fontSize: "0.8125rem" }} className={`w-24 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("department")}>
              {t("common.department")}
            </label>
            <p className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("department")}>{translateDepartment(t, requesterDeptCdName) || "-"}</p>
          </div> */}
          <div className="flex items-center" data-testid={fieldContainerTestId("last-modified")}>
            <label style={{ fontSize: "0.8125rem" }} className={`w-24 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("last-modified")}>
              {t("common.last-modified-date")}
            </label>
            <p className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("last-modified")}>{formattedUpdatedAt}</p>
          </div>
          <div className="flex items-center gap-4" data-testid={fieldContainerTestId("reverification-due-date")}>
            <label style={{ fontSize: "0.8125rem" }} className={`w-24 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("reverification-due-date")}>
              {t("common.reverification-due-date")}
            </label>
            <p className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("reverification-due-date")}>{formattedReverificationDueDt}</p>
          </div>
          {showVendorInfo && (
            <div className="col-span-full mt-2 grid grid-cols-5 gap-2 border-t border-white/10 pt-3" data-testid={tid("vendor-info-row")}>
              <div className="flex min-w-0 items-center gap-2" data-testid={fieldContainerTestId("vendor-company-name")}>
                <label style={{ fontSize: "0.8125rem" }} className={`shrink-0 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("vendor-company-name")}>
                  {t("project.sbom.vendor-company-name")}
                </label>
                <p className={`min-w-0 truncate text-sm font-medium ${colors.textPrimary}`} title={vendorCompanyName || "-"} data-testid={fieldValueTestId("vendor-company-name")}>{vendorCompanyName || "-"}</p>
              </div>
              <div className="flex min-w-0 items-center gap-2" data-testid={fieldContainerTestId("vendor-contact-name")}>
                <label style={{ fontSize: "0.8125rem" }} className={`shrink-0 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("vendor-contact-name")}>
                  {t("project.sbom.vendor-contact-name")}
                </label>
                <p className={`min-w-0 truncate text-sm font-medium ${colors.textPrimary}`} title={vendorContactName || "-"} data-testid={fieldValueTestId("vendor-contact-name")}>{vendorContactName || "-"}</p>
              </div>
              <div className="flex min-w-0 items-center gap-2" data-testid={fieldContainerTestId("vendor-contact-email")}>
                <label style={{ fontSize: "0.8125rem" }} className={`shrink-0 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("vendor-contact-email")}>
                  {t("project.sbom.vendor-contact-email")}
                </label>
                <p className={`min-w-0 truncate text-sm font-medium ${colors.textPrimary}`} title={vendorContactEmail || "-"} data-testid={fieldValueTestId("vendor-contact-email")}>{vendorContactEmail || "-"}</p>
              </div>
              <div className="flex min-w-0 items-center gap-2" data-testid={fieldContainerTestId("vendor-address")}>
                <label style={{ fontSize: "0.8125rem" }} className={`shrink-0 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("vendor-address")}>
                  {t("project.sbom.vendor-address")}
                </label>
                <p className={`min-w-0 truncate text-sm font-medium ${colors.textPrimary}`} title={vendorAddress || "-"} data-testid={fieldValueTestId("vendor-address")}>{vendorAddress || "-"}</p>
              </div>
              <div className="flex min-w-0 items-center gap-2" data-testid={fieldContainerTestId("vendor-submission-date")}>
                <label style={{ fontSize: "0.8125rem" }} className={`shrink-0 font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("vendor-submission-date")}>
                  {t("project.sbom.vendor-submission-date")}
                </label>
                <p className={`min-w-0 truncate text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("vendor-submission-date")}>{formattedVendorSubmissionDate}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BasicInfoSection;
